import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import {
  SEVEN_WALLS,
  browserFreezeSignals,
  createBrowserClientCabinet,
  listBrowserClientCabinets,
  listBrowserIntegrityEvents,
} from "../../js/integrity/browser";

export default function AdminIntegrity() {
  const { session } = useAuth();
  const [pulse, setPulse] = useState(0);
  const [notice, setNotice] = useState("");

  const freeze = useMemo(() => browserFreezeSignals(session), [session, pulse]);
  const cabinets = useMemo(() => listBrowserClientCabinets(), [pulse]);
  const events = useMemo(() => listBrowserIntegrityEvents(24), [pulse]);

  function runClientCabinet() {
    const cabinet = createBrowserClientCabinet(session, {
      note: "Admin integrity desk — client-side cabinet",
    });
    setNotice(`Client cabinet ${cabinet.id} downloaded.`);
    setPulse((n) => n + 1);
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Deployment Integrity</h1>
          <p className="page-lead">
            The Seven Walls of Steel protect REChecklist from Client- or Vendor-side failures that
            could lose transaction, compliance, or legal packet data. CLI gates block release when
            any wall fails. This desk is read-only status plus a client-cabinet export.
          </p>
        </div>
      </div>

      {notice ? <p className="confirm-banner">{notice}</p> : null}

      <section className="desk-panel card">
        <h2>Seven Walls of Steel</h2>
        <ol className="integrity-wall-list">
          {SEVEN_WALLS.map((wall) => (
            <li key={wall.wall}>
              <strong>
                Wall {wall.wall} — {wall.name}
              </strong>
              <p className="muted">{wall.summary}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="desk-panel card">
        <h2>Freeze signals (this device)</h2>
        <p className="muted">
          Open packets on this signed-in desk trip Wall 5 auto-freeze for gated releases. Scheduled
          deploy windows are Tue–Thu 05:00–07:00 America/Los_Angeles (see{" "}
          <code>integrity/config.js</code>).
        </p>
        <ul>
          <li>Open packets: {freeze.openPackets}</li>
          <li>Routing active: {freeze.routingActive ? "yes" : "no"}</li>
        </ul>
        <p className="muted">
          Run <code>npm run integrity:check</code> or <code>npm run release:gated</code> from the
          repo root. Use <code>npm run integrity:bootstrap</code> once to seal local = staging =
          production fingerprints after a clean build.
        </p>
      </section>

      <section className="desk-panel card">
        <h2>Dual-side backups</h2>
        <p className="muted">
          Vendor cabinets are created by the CLI (<code>integrity:backup:vendor</code>) into
          primary / secondary / cold / immutable tiers. Client cabinets can be exported here for
          the signed-in desk.
        </p>
        <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
          <button type="button" className="primary-btn" onClick={runClientCabinet}>
            Download client cabinet
          </button>
        </div>
        {cabinets.length ? (
          <ul style={{ marginTop: "1rem" }}>
            {cabinets
              .slice()
              .reverse()
              .map((c) => (
                <li key={c.id}>
                  {c.id} · {c.createdAt}
                </li>
              ))}
          </ul>
        ) : (
          <p className="muted" style={{ marginTop: "1rem" }}>
            No browser client cabinets yet on this device.
          </p>
        )}
      </section>

      <section className="desk-panel card">
        <h2>Recent integrity events (this browser)</h2>
        {events.length ? (
          <ul>
            {events.map((ev) => (
              <li key={ev.id}>
                <code>{ev.action_type}</code> · {ev.timestamp}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No browser integrity events yet.</p>
        )}
      </section>
    </AppLayout>
  );
}
